import _ from 'lodash';
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneIcon from '@mui/icons-material/Phone';
import { EMAIL_REGEX, PHONE_NUMBER_REGEX } from '../../../utility/inputUtils';
import { Launch } from '@mui/icons-material';
import AppLink from '../AppLink/AppLink';

interface ITextFieldWitLinkProps extends Omit<TextFieldProps, 'value'> {
  linkType: 'email' | 'phone' | 'internal';
  value: string;
  to?: string;
}

// It defines which icon to use for each linkType
const ICON_MAP = {
  email: MailOutlineIcon,
  phone: PhoneIcon,
  internal: Launch,
};

// It defines which regex to use for each linkType
const REGEX_MAP = {
  email: EMAIL_REGEX,
  phone: PHONE_NUMBER_REGEX,
  internal: /.*/,
};

const HREF_MAP = {
  email: 'mailto:',
  phone: 'tel:',
  internal: '',
};

const validateWithRegex = (text: string, regex: RegExp): boolean => {
  if (_.isEmpty(text)) return false;

  return regex.test(text);
};

export default function TextFieldWithLink({
  linkType,
  value,
  to,
  ...rest
}: ITextFieldWitLinkProps) {
  const isValid = validateWithRegex(value, REGEX_MAP[linkType]);
  const url = (() => {
    if (!isValid) return '';
    if (linkType === 'internal') return to ?? '';

    return `${HREF_MAP[linkType]}${value}`;
  })();

  const RenderIcon = ICON_MAP[linkType];
  const LinkComponent = linkType === 'internal' ? AppLink : Link;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <TextField
        value={value}
        {...rest}
      />
      <LinkComponent
        to={url}
        style={{ alignContent: 'center', pointerEvents: isValid ? 'auto' : 'none' }}
      >
        <RenderIcon sx={{ color: isValid ? 'black' : 'grey' }} />
      </LinkComponent>
    </Box>
  );
}
